import React, { useState } from 'react';
import { Sparkles, Plus, Trash2, ArrowRight, Activity, HelpCircle, AlertTriangle, Zap, FileSpreadsheet, Award } from 'lucide-react';
import { ServiceIcon } from './Views';
import { CohortOnboardingModal } from './CohortOnboardingModal';
import { getCohortSettings, CohortAccessSettings } from '../utils/cohortService';

interface OnboardingViewProps {
  onComplete: (streams: string[]) => void;
  onLoadDemo: () => void;
  onOpenReconciler?: () => void;
  showToast?: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
}

export function OnboardingView({ onComplete, onLoadDemo, onOpenReconciler, showToast }: OnboardingViewProps) {
  const [streams, setStreams] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [isCohortModalOpen, setIsCohortModalOpen] = useState(false);
  const [cohortSettings, setCohortSettings] = useState<CohortAccessSettings>(getCohortSettings());

  // 1-Click Founder Archetype Starter Packs
  const ARCHETYPES = [
    {
      id: 'consultant',
      title: 'Solo Consultant / Advisor',
      streams: ['Consulting', 'Monthly Retainer'],
      desc: 'Hourly strategy & recurring advisory retainers'
    },
    {
      id: 'studio',
      title: 'Creative / Dev Studio',
      streams: ['Client Projects', 'Support Retainer'],
      desc: 'Milestone deliverables & monthly maintenance'
    },
    {
      id: 'creator',
      title: 'Coach / Creator',
      streams: ['VIP Strategy Sessions', 'Digital Products'],
      desc: '1-on-1 private coaching & digital asset sales'
    }
  ];

  // Suggestions for rapid setup
  const suggestions = [
    'Consulting',
    'Content Creation',
    'Fitness Coaching',
    'Retreats',
    'SaaS Subscription',
    'E-commerce'
  ];

  const handleAddStream = (name: string) => {
    const cleanName = name.trim();
    if (!cleanName) return;
    if (streams.some(s => s.toLowerCase() === cleanName.toLowerCase())) {
      setError('You already added this stream.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setStreams(prev => [...prev, cleanName]);
    setInputValue('');
  };

  const handleRemoveStream = (index: number) => {
    setStreams(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      handleAddStream(inputValue);
    } else if (streams.length > 0) {
      onComplete(streams);
    }
  };

  const getGreeting = () => {
    const hr = new Date().getHours();
    if (hr < 12) return 'Good morning';
    if (hr < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4 animate-fade-in">
      <div className="w-full max-w-xl bg-[#151819] border border-[#1F2426] rounded-2xl p-6 sm:p-10 shadow-2xl relative overflow-hidden space-y-8">
        
        {/* Subtle high-contrast accent lines */}
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-teal-500/10 via-[#14B8A6] to-teal-500/10" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#14B8A6]/5 rounded-full blur-3xl pointer-events-none" />

        {/* Greeting & Prompt */}
        <div className="space-y-2 text-center sm:text-left">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-[#14B8A6]/10 text-[#14B8A6] rounded-full text-[10px] font-bold tracking-widest uppercase">
              🚀 10-Minute Financial Clarity
            </div>
            
            {/* COHORT SPONSORSHIP BADGE / TRIGGER */}
            {cohortSettings.isCohortMember ? (
              <button
                type="button"
                onClick={() => setIsCohortModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-500/20 border border-teal-500/40 text-teal-300 text-[10px] font-mono font-bold hover:bg-teal-500/30 transition-all cursor-pointer"
              >
                <Award size={12} className="text-teal-400" />
                <span>Sponsored by {cohortSettings.cohortName}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsCohortModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1F2426] hover:bg-teal-950/40 border border-[#2A2F31] hover:border-teal-500/40 text-gray-300 hover:text-teal-300 text-[10px] font-mono transition-all cursor-pointer"
              >
                <Award size={12} className="text-teal-400" />
                <span>Have a Cohort / Accelerator Voucher?</span>
              </button>
            )}
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-extrabold text-white tracking-tight">
            {getGreeting()}
          </h2>
          <p className="text-sm text-gray-400">
            In 10 minutes, know what you actually keep after expenses and tax reserves. Pick a 1-click archetype below or enter your own custom streams.
          </p>
        </div>

        {/* 1-Click Fast-Track Archetypes */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              ⚡ 1-Click Starter Packs (Instant Launch)
            </span>
            <span className="text-[10px] text-teal-400 font-mono">Zero typing needed</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            {ARCHETYPES.map((arch) => (
              <button
                key={arch.id}
                type="button"
                onClick={() => onComplete(arch.streams)}
                className="p-3 bg-[#0C0E0F] hover:bg-[#121A19] border border-[#2A2F31] hover:border-[#14B8A6]/60 rounded-xl text-left transition-all group cursor-pointer space-y-1"
              >
                <div className="text-xs font-bold text-white group-hover:text-[#14B8A6] transition-colors flex items-center justify-between">
                  <span>{arch.title}</span>
                  <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="text-[11px] text-gray-400 leading-tight">
                  {arch.desc}
                </p>
                <div className="pt-1 flex flex-wrap gap-1">
                  {arch.streams.map((s) => (
                    <span key={s} className="px-1.5 py-0.5 rounded bg-[#14B8A6]/10 text-[#14B8A6] text-[9px] font-mono">
                      {s}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Or Drop Statement via AI Reconciler */}
        {onOpenReconciler && (
          <div className="p-3.5 bg-[#0C1213] border border-[#14B8A6]/30 rounded-xl flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-lg bg-[#14B8A6]/20 text-[#14B8A6] flex items-center justify-center text-xs shrink-0">
                <Zap size={14} />
              </span>
              <div>
                <p className="text-xs font-bold text-white">Have a bank statement PDF or CSV?</p>
                <p className="text-[11px] text-gray-400">The Multi-Format AI Reconciler auto-detects your income streams in 0.05s.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onOpenReconciler}
              className="px-3 py-1.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-lg text-xs shrink-0 transition cursor-pointer"
            >
              Drop Statement
            </button>
          </div>
        )}

        {/* Input Form for Custom Streams */}
        <form onSubmit={handleSubmit} className="space-y-4 pt-2 border-t border-[#1F2426]">
          <div className="space-y-2">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
              Add Income Stream
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="e.g. Content Creation or Consulting"
                className="flex-1 min-w-0 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] transition text-sm font-medium"
                autoFocus
              />
              <button
                type="button"
                onClick={() => handleAddStream(inputValue)}
                disabled={!inputValue.trim()}
                className="px-4 bg-[#14B8A6] hover:bg-[#0EA5A4] disabled:opacity-40 disabled:hover:bg-[#14B8A6] text-black rounded-xl font-bold transition flex items-center justify-center cursor-pointer"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {error && (
            <p className="text-xs text-rose-400 flex items-center gap-1">
              <AlertTriangle size={12} /> {error}
            </p>
          )}

          {/* Quick Suggestions */}
          <div className="space-y-2">
            <span className="text-[10px] text-gray-500 font-mono font-bold uppercase block">
              Popular Suggestions:
            </span>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion) => {
                const isAdded = streams.some(s => s.toLowerCase() === suggestion.toLowerCase());
                return (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => handleAddStream(suggestion)}
                    disabled={isAdded}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 active:scale-95 flex items-center gap-1 cursor-pointer ${
                      isAdded
                        ? 'bg-[#14B8A6]/10 text-[#14B8A6]/40 border-transparent cursor-not-allowed'
                        : 'bg-[#0C0E0F] text-gray-300 border-[#1F2426] hover:border-[#14B8A6] hover:text-white'
                    }`}
                  >
                    <span>{suggestion}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </form>

        {/* Added Streams List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs font-bold text-gray-500 uppercase tracking-wider">
            <span>Your Active Streams ({streams.length})</span>
            {streams.length === 0 && <span className="text-amber-500 font-mono text-[10px] lowercase italic">Minimum 1 required</span>}
          </div>

          {streams.length > 0 ? (
            <div className="bg-[#0C0E0F] border border-[#1F2426] rounded-xl divide-y divide-[#1F2426] overflow-hidden max-h-[160px] overflow-y-auto">
              {streams.map((stream, idx) => (
                <div key={idx} className="flex items-center justify-between p-3.5 text-sm">
                  <div className="flex items-center gap-3">
                    <ServiceIcon type={stream} className="w-4 h-4 text-[#14B8A6]" />
                    <span className="text-gray-200 font-medium">{stream}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveStream(idx)}
                    className="p-1.5 text-gray-500 hover:text-rose-400 hover:bg-rose-500/5 rounded-lg transition-colors cursor-pointer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#0C0E0F]/50 border border-dashed border-[#1F2426] rounded-xl p-8 text-center text-xs text-gray-500">
              Your custom streams will be displayed here. Add one above to activate your sandbox.
            </div>
          )}
        </div>

        {/* Action Controls */}
        <div className="pt-4 border-t border-[#1F2426]/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          
          <button
            type="button"
            onClick={onLoadDemo}
            className="text-xs font-mono text-teal-400 hover:text-teal-300 hover:underline transition-all cursor-pointer"
          >
            💡 Try Sandbox with Demo Data instead
          </button>

          <button
            type="button"
            onClick={() => onComplete(streams)}
            disabled={streams.length === 0}
            className="w-full sm:w-auto px-6 h-12 bg-[#14B8A6] hover:bg-[#0EA5A4] disabled:opacity-40 disabled:hover:bg-[#14B8A6] disabled:cursor-not-allowed text-black font-extrabold rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 cursor-pointer text-sm"
          >
            Launch Sandbox
            <ArrowRight size={16} />
          </button>
        </div>

      </div>

      {/* Cohort Sponsorship Onboarding Modal */}
      <CohortOnboardingModal
        isOpen={isCohortModalOpen}
        onClose={() => setIsCohortModalOpen(false)}
        onActivated={(newSettings) => {
          setCohortSettings(newSettings);
          setIsCohortModalOpen(false);
          if (showToast) showToast(`🎓 ${newSettings.cohortName} sponsorship activated!`, 'success');
        }}
        showToast={showToast}
      />
    </div>
  );
}
